import { useEffect, useState } from "react";
import useInterceptorPrompt from "../../../hooks/useInterceptorPrompt";
import MarkDownEditor from "../../shared/markdownEditor/MarkdownEditor";
import TranslatableMarkdownEditor from "../../shared/multilingualism/TranslatableMarkdownEditor";
import { get, isEmpty } from "lodash";
import { Control, Controller, useFormContext } from "react-hook-form";
import classNames from "classnames";

interface ClosableTixxtComposerProps {
  onOpen: () => void;
  open: boolean;
  name: string;
  control?: Control<any>;
  required?: boolean | string;
  handleFullscreen?: () => void;
  placeholder?: string;
  /**
   * Enables the translation popover next to the editor. When true, the content
   * and the translations object (at `translationsPath`) are kept in sync via
   * `TranslatableMarkdownEditor`'s colocated pattern.
   */
  translatable?: boolean;
  /**
   * Form path to the translations hash. Defaults to "object.translations" —
   * the activity composer's root for the activity object's translations.
   */
  translationsPath?: string;
  /**
   * Field name stored inside the translations hash (e.g. `translations.en.<field>`).
   * Defaults to "content".
   */
  translationField?: string;
}

export default function ClosableTixxtComposer({
  onOpen,
  open,
  name,
  control,
  required = false,
  placeholder,
  translatable = false,
  translationsPath = "object.translations",
  translationField = "content",
}: ClosableTixxtComposerProps) {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [closed, setClosed] = useState(!open);
  const content = watch(name);

  useInterceptorPrompt(
    I18n.t("js.composer.interceptor_prompt"),
    !isEmpty(content),
  );

  function onOpenHandler(e) {
    e.preventDefault();
    setClosed(false);
    onOpen();
  }

  useEffect(() => {
    setClosed(!open);
  }, [open]);

  return closed ? (
    <button
      key="dummy"
      className={`ClosableTixxtComposer form-control closed text-left text-gray-400`}
      onClick={onOpenHandler}
    >
      <span className="placeholder">{placeholder}</span>
    </button>
  ) : (
    <Controller
      name={name}
      control={control}
      rules={{
        required,
      }}
      render={({ field }) => {
        // The post editor ("Beitragseditor") is taller and freely resizable on
        // the web; the mobile app keeps its fixed-height (h-90) layout.
        const resizable = !window.isApp;
        const wrapper = (
          <div
            className={classNames("min-h-48 grow", {
              "field-error field-error-live": !!get(errors, name),
              "h-90": window.isApp,
            })}
          >
            {translatable ? (
              <TranslatableMarkdownEditor
                value={field.value || ""}
                translationsValue={watch(translationsPath) || {}}
                onValueChange={(value, translations) => {
                  setValue(name, value);
                  setValue(translationsPath, translations);
                }}
                field={translationField}
                placeholder={placeholder}
                enableTixxtMentions
                resizable={resizable}
              />
            ) : (
              <MarkDownEditor
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                placeholder={placeholder}
                enableTixxtMentions
                resizable={resizable}
              />
            )}
          </div>
        );
        return wrapper;
      }}
    />
  );
}
